import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components/native';
import {Animated, LayoutChangeEvent, Platform} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import {CustomScrollViewProps} from '../../interfaces/components/customScrollView';
import {Space} from '../../screens/content/home';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

const Container = styled.View`
  width: 100%;
  overflow: hidden;
`;
const Scroll = styled(ScrollView)`
  padding: ${({theme}) => theme.text.s55}px;
  ${Platform.OS === 'web' && css`
    padding-right: ${({theme}) => theme.text.s55 + 8}px;
  `}
`;
const IndicatorWrapper = styled.View`
  background-color: rgba(0, 0, 0, 0.6);
  position: absolute;
  border-radius: ${({theme}) => theme.text.s12 / 2}px;
  top: ${({theme}) => theme.text.s4}px;
  right: ${({theme}) => theme.text.s12}px;
  z-index: 1;
  width: ${({theme}) => theme.text.s12}px;
  height: 85%;
`;
const Indicator = styled(Animated.View)`
  background-color: ${({theme}) => theme.text.light};
  width: ${({theme}) => theme.text.s12}px;
  border-radius: ${({theme}) => theme.text.s12 / 2}px;
  position: absolute;
  top: ${({theme}) => theme.text.s4}px;
  right: ${({theme}) => theme.text.s12}px;
  z-index: 2;
`;

const CustomScrollView: React.FC<CustomScrollViewProps> = ({
  children,
  style,
  ...props
}) => {
  const {hdp} = useResponsiveDimensions();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const indicator = useRef(new Animated.Value(0)).current;
  const [wholeHeight, setWholeHeight] = useState(1);
  const [visibleHeight, setVisibleHeight] = useState(0);
  const [indicatorSize, setIndicatorSize] = useState(0);
  const [difference, setDifference] = useState(0);

  useEffect(() => {
    const tempSize =
      wholeHeight > visibleHeight
        ? (visibleHeight * visibleHeight) / wholeHeight
        : visibleHeight;
    const tempDifference =
      visibleHeight > tempSize ? visibleHeight - tempSize : 1;
    setIndicatorSize(tempSize);
    setDifference(tempDifference);
  }, [visibleHeight, wholeHeight]);

  const indicatorStyle = {
    height: indicatorSize,
    transform: [
      {
        translateY: Animated.multiply(
          indicator,
          visibleHeight / wholeHeight,
        ).interpolate({
          inputRange: [0, difference],
          outputRange: [0, difference],
          extrapolate: 'clamp',
        }),
      },
    ],
  };
  return (
    <Container style={style}>
      <Scroll
        alwaysBounceVertical
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        onContentSizeChange={(_: any, height: number) => {
          setWholeHeight((height * 85) / 100);
        }}
        onLayout={(e: LayoutChangeEvent) =>
          setVisibleHeight((e?.nativeEvent?.layout?.height * 85) / 100 || 0)
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{nativeEvent: {contentOffset: {y: indicator}}}],
          {
            useNativeDriver: false,
          },
        )}
        {...props}>
        {children}
        <Space bottomInset={bottomInset} extraSpace={hdp(5)} />
      </Scroll>
      {Platform.OS !== 'web' && visibleHeight > indicatorSize && (
        <>
          <IndicatorWrapper />
          <Indicator style={indicatorStyle} />
        </>
      )}
    </Container>
  );
};

export default CustomScrollView;
